import type { ProductVariant } from '~~/types/commercetools';
import { NOTIFICATION_STATUS } from '~~/types/components/Notification/NotificationStatus';
import { isTestProduct } from '~~/helpers/product/isTestProduct';
import { getLineItemFromSku } from '~~/helpers/cart/getLineItemFromSku';
import { SCROLL_BEHAVIOR_SMOOTH } from '~~/constants/ui';
import type { EventSourceLocation } from '~~/types/integrations/tagManager/TagManagerIntegration';
import { MAX_SINGLE_PRODUCT_CART_QUANTITY } from '~~/constants/checkout';

type AddToCartParams = {
  product: ProductVariant;
  quantity: number;
  triggeredIn?: EventSourceLocation;
  shouldOpenCart?: boolean;
};

/**
 * Custom composable for adding product to cart from add to cart button
 */
export const useAddToCart = (slug: string, sku: string) => {

  const i18n = useI18n();
  const { error: cartError, cart, addItem } = useCartExtended();
  const { setIsGuaranteeActionBeingClicked } = useLineItems();
  const { openCartSidebars } = useCartSidebars();
  const { send: sendUiNotification } = useUiNotification();
  const { stocks: stocksFetched } = useStock(slug);

  const stock = computed(() => stocksFetched.value?.[0]?.availableQuantity || 0);

  const quantityInCart = computed(() => {

    return getLineItemFromSku(cart.value, sku)?.quantity || 0;

  });

  const currentlyRemainingStock = computed(() => {

    return stock.value - quantityInCart.value;

  });

  const maxSingleProductQuantity = computed(() => {

    return MAX_SINGLE_PRODUCT_CART_QUANTITY - quantityInCart.value;

  });

  const availableQuantity = computed(() => {

    return Math.min(maxSingleProductQuantity.value, currentlyRemainingStock.value);

  });

  const isQuantitySelectionPossible = computed(() => {

    return availableQuantity?.value > 0 || (sku && isTestProduct(sku));

  });

  const addToCartButtonText = computed(() => {

    return isQuantitySelectionPossible.value ? 'Add to cart' : 'out-of-stock';

  });

  const addToCart =
    async ({ product, quantity, triggeredIn, shouldOpenCart }: AddToCartParams) => {

      window.scrollTo({ top: 0, behavior: SCROLL_BEHAVIOR_SMOOTH });
      await addItem({
        product,
        quantity,
        triggeredIn,
      });
      setIsGuaranteeActionBeingClicked(false);

      if (!shouldOpenCart) {

        openCartSidebars({ product, sku });

      }

      checkForErrors();

    };

  const hasAddToCartError = computed(() => !!cartError.value.addItem);

  const checkForErrors = () => {

    if (hasAddToCartError.value) {

      sendUiNotification({
        message: i18n.t('There was a problem with adding product to the cart. Please try again.'),
        id: 'cart-adding-problem',
        type: NOTIFICATION_STATUS.error,
        dismissible: true,
      });

    }

  };

  return {
    addToCart,
    availableQuantity,
    isQuantitySelectionPossible,
    addToCartButtonText,
    stock,
    quantityInCart,
  };

};

export default useAddToCart;
